import { useTranslation } from 'react-i18next';
import type { FieldMetaProps } from 'formik';
import { exceedsTextAreaMaxLength, TEXTAREA_MAX_LENGTH } from '../utils';

type Options = {
    // Show the character counter at all times, not only when over the limit
    showCounter: boolean;
    isReadOnly: boolean;
};

/**
 * Helper text and error state for free-text fields limited by Camunda's
 * string variable length. The form only validates on submit, so the length is
 * checked locally to give feedback while typing; the schema enforces the same
 * limit on submit.
 */
export const useTextLengthFeedback = (
    value: unknown,
    meta: Pick<FieldMetaProps<unknown>, 'touched' | 'error'>,
    { showCounter, isReadOnly }: Options,
): { helperText: string; error: boolean } => {
    const { t } = useTranslation();

    const text = typeof value === 'string' ? value : String(value ?? '');
    const isTooLong = !isReadOnly && exceedsTextAreaMaxLength(text);
    const maxLengthMessage = t('formGenerator.maxLengthExceeded', {
        max: TEXTAREA_MAX_LENGTH,
    });
    // A max-length error from the last submit is stale once the text is shortened
    const schemaError =
        meta.touched && meta.error !== maxLengthMessage
            ? meta.error
            : undefined;
    const errorMessage = isTooLong ? maxLengthMessage : schemaError;

    const characterCount =
        !isReadOnly && (showCounter || isTooLong)
            ? t('formGenerator.characterCount', {
                  length: text.length,
                  max: TEXTAREA_MAX_LENGTH,
              })
            : '';

    const helperText =
        errorMessage && characterCount
            ? `${errorMessage} (${characterCount})`
            : (errorMessage ?? characterCount);

    return { helperText, error: Boolean(errorMessage) };
};
